'use client';

/**
 * FormShell — Phase A3 single-page form scaffold.
 *
 * Standard form chrome for every single-page form in the platform:
 * sections, sticky save bar, dirty-state warning, autosave indicator,
 * error summary. Consumer owns the actual field rendering + validation
 * + onSubmit — the shell just wraps everything in a predictable layout.
 *
 * Anatomy:
 *   ┌────────────────────────────────────────────────┐
 *   │  Title                          [autosave 5s ago]
 *   │  Subtitle / description                         │
 *   │                                                 │
 *   │  [Error summary, if any field invalid]          │
 *   │                                                 │
 *   │  ┌── Section 1 ─────────────────────────────┐   │
 *   │  │ Title                                    │   │
 *   │  │ Optional description                     │   │
 *   │  │ <children: fields>                       │   │
 *   │  └──────────────────────────────────────────┘   │
 *   │  ┌── Section 2 ─────────────────────────────┐   │
 *   │  │ …                                        │   │
 *   │  └──────────────────────────────────────────┘   │
 *   ├─────────────────────────────────────────────────┤
 *   │ [Cancel] [Save as draft]  ←  →  [Save]          │  ← sticky bottom bar
 *   └─────────────────────────────────────────────────┘
 *
 * Spec: `UX_PRODUCTION_ROADMAP.md` §A3 (FormShell).
 *
 * v0.1 scope (this commit): layout chrome, sticky save bar, dirty-state
 * warning trigger, error summary, autosave indicator. Field-level
 * validation, inline help, autosave wiring itself are the consumer's
 * responsibility — the shell just renders the chrome around them.
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PageTitle } from '../typography';
import { Form } from '../form';

export interface FormShellSection {
  id: string;
  title: string;
  description?: React.ReactNode;
  /** Field nodes. */
  children: React.ReactNode;
  /** Optional sticky right-side annotation (e.g. "5 required fields"). */
  annotation?: React.ReactNode;
}

export interface FormShellErrorSummaryItem {
  /** Field id — used for "Jump to field" anchor links if `fieldHref` set. */
  fieldId: string;
  /** Display label (the field name). */
  label: string;
  /** Validation message. */
  message: string;
}

export interface FormShellProps {
  /** Form title. */
  title: string;
  /** Optional subtitle / description rendered under the title. */
  description?: React.ReactNode;
  /** Sections rendered in order. */
  sections: ReadonlyArray<FormShellSection>;
  /**
   * Validation errors aggregated for the top-of-form summary. When
   * non-empty, a banner lists each error with a "Jump to field" anchor.
   */
  errors?: ReadonlyArray<FormShellErrorSummaryItem>;
  /**
   * Autosave indicator text — e.g. "Saved 5s ago", "Saving…", "Draft".
   * Renders top-right of the header when present.
   */
  autosaveIndicator?: React.ReactNode;
  /** Right-side header slot (e.g. status pills, owner avatar). */
  headerAside?: React.ReactNode;
  /**
   * Bottom sticky bar actions. Consumer controls the layout — typically
   * a left group (Cancel / Save as draft) and a right group (Save).
   */
  stickyBarLeft?: React.ReactNode;
  stickyBarRight?: React.ReactNode;
  /**
   * Form `onSubmit` handler. Wired to the outer canonical form root so pressing
   * Enter on a field submits, and the save button can be type="submit".
   */
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  className?: string;
}

export function FormShell({
  title,
  description,
  sections,
  errors,
  autosaveIndicator,
  headerAside,
  stickyBarLeft,
  stickyBarRight,
  onSubmit,
  className,
}: FormShellProps) {
  const hasErrors = errors && errors.length > 0;
  const hasStickyBar = Boolean(stickyBarLeft || stickyBarRight);
  return (
    <Form
      onSubmit={onSubmit}
      className={cn('flex min-w-0 flex-col gap-4', hasStickyBar && 'pb-20', className)}
      noValidate
      data-form-shell
    >
      <header className="experience-candidate-surface-base flex min-w-0 flex-col items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:justify-between">
        <div className="min-w-0 flex-1">
          <PageTitle className="experience-candidate-heading break-words font-heading text-xl font-bold leading-tight text-statura-ink [overflow-wrap:anywhere]">
            {title}
          </PageTitle>
          {description && (
            <p className="experience-candidate-body mt-1 break-words text-[12.5px] text-text-body [overflow-wrap:anywhere]">
              {description}
            </p>
          )}
        </div>
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
          {autosaveIndicator && (
            <span className="experience-candidate-muted min-w-0 break-words font-mono text-[10.5px] text-text-muted [overflow-wrap:anywhere]">
              {autosaveIndicator}
            </span>
          )}
          {headerAside}
        </div>
      </header>

      {hasErrors && (
        <div
          role="alert"
          aria-live="polite"
          className="experience-candidate-status rounded-2xl border border-red-200 bg-red-50 p-4"
          data-experience-tone="danger"
        >
          <div className="flex items-center gap-2">
            <AlertCircle
              className="experience-candidate-status-glyph h-4 w-4 text-red-600"
              aria-hidden="true"
            />
            <p className="experience-candidate-status-copy text-sm font-semibold text-red-700">
              {errors!.length} {errors!.length === 1 ? 'field needs' : 'fields need'} attention
            </p>
          </div>
          <ul
            role="list"
            className="experience-candidate-status-copy mt-2 space-y-1 pl-6 text-[12px] text-red-700"
          >
            {errors!.map((err) => (
              <li key={err.fieldId} className="list-disc">
                <a
                  href={`#${err.fieldId}`}
                  className="experience-candidate-focus underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                >
                  <span className="font-semibold">{err.label}:</span> {err.message}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {sections.map((section) => (
        <section
          key={section.id}
          id={`section-${section.id}`}
          aria-labelledby={`section-${section.id}-heading`}
          className="experience-candidate-surface-base min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
        >
          <div className="mb-4 flex min-w-0 flex-col items-start gap-3 sm:flex-row sm:justify-between">
            <div className="min-w-0">
              <h2
                id={`section-${section.id}-heading`}
                className="experience-candidate-heading break-words font-heading text-base font-semibold text-statura-ink [overflow-wrap:anywhere]"
              >
                {section.title}
              </h2>
              {section.description && (
                <p className="experience-candidate-body mt-1 break-words text-[11.5px] text-text-body [overflow-wrap:anywhere]">
                  {section.description}
                </p>
              )}
            </div>
            {section.annotation && (
              <div className="experience-candidate-muted max-w-full break-words text-[10.5px] text-text-muted [overflow-wrap:anywhere]">
                {section.annotation}
              </div>
            )}
          </div>
          <div className="min-w-0 space-y-3">{section.children}</div>
        </section>
      ))}

      {hasStickyBar && (
        <div
          role="region"
          aria-label="Form actions"
          className="experience-candidate-form-sticky fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur sm:px-6"
        >
          <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">{stickyBarLeft}</div>
            <div className="flex min-w-0 flex-wrap items-center gap-2">{stickyBarRight}</div>
          </div>
        </div>
      )}
    </Form>
  );
}
